import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { Activity } from "lucide-react";
import { useEffect, useState } from "react";
import {
  Create,
  Datagrid,
  DateField,
  Edit,
  List,
  NumberField,
  SelectInput,
  TextField,
  TextInput,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from "react-admin";
import { apiURL, authClient, authToken } from "../../server";
import { bulkEmailResources } from "./common";

const runStates = [
  "scheduled",
  "resolving",
  "ready",
  "dispatching",
  "completed",
  "failed",
  "canceled",
];

const recipientStates = [
  "pending",
  "dispatching",
  "sent",
  "failed",
  "unknown",
  "suppressed",
  "canceled",
];
const recipientsPerPage = 200;

const runFilters = [
  <SelectInput
    key="state"
    source="state"
    choices={runStates.map((state) => ({ id: state, name: state }))}
  />,
  <TextInput key="sending_id" source="sending_id" label="Sending ID" />,
];

function RunList() {
  return (
    <List filters={runFilters} sort={{ field: "id", order: "DESC" }}>
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="id" />
        <TextField source="state" />
        <TextField source="run_type" />
        <NumberField source="campaign_id" />
        <NumberField source="sending_id" />
        <NumberField source="recipient_count" />
        <NumberField source="sent_count" />
        <NumberField source="failed_count" />
        <NumberField source="unknown_count" />
        <DateField source="scheduled_for" showTime />
        <DateField source="created_at" showTime />
      </Datagrid>
    </List>
  );
}

function RunLauncher() {
  const notify = useNotify();
  const redirect = useRedirect();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [sendings, setSendings] = useState([]);
  const [sending, setSending] = useState();
  const [scheduledFor, setScheduledFor] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    authClient
      .get("/admin/bulk-email/campaigns?_start=0&_end=500")
      .then((response) => setCampaigns(response.json))
      .catch((error) =>
        notify(error.body?.message || error.message, { type: "error" }),
      );
  }, [notify]);

  const selectCampaign = async (id) => {
    setCampaignId(id);
    setSending(undefined);
    const response = await authClient.get(
      `/admin/bulk-email/campaigns/${id}/sendings?_start=0&_end=500`,
    );
    setSendings(response.json);
  };

  const selectSending = async (id) => {
    const response = await authClient.get(
      `/admin/bulk-email/campaigns/${campaignId}/sendings/${id}`,
    );
    setSending(response.json);
  };

  const launch = async () => {
    const recipients =
      sending.latest_validation?.summary?.effective_recipients || 0;
    if (!window.confirm(`Launch this run for ${recipients} recipients?`))
      return;
    try {
      const response = await authClient.post(
        `/admin/bulk-email/campaigns/${campaignId}/sendings/${sending.id}/runs`,
        {
          confirmed_validation_id: sending.latest_validation.id,
          confirmed_definition_hash: sending.current_definition_hash,
          confirmed_counts: true,
          scheduled_for: scheduledFor
            ? new Date(scheduledFor).toISOString()
            : null,
          unsubscribe_override_reason: overrideReason || undefined,
        },
      );
      notify(scheduledFor ? "Run scheduled" : "Run launched", {
        type: "success",
      });
      redirect("edit", bulkEmailResources.runs, response.json.id);
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const warnings = sending?.latest_validation?.warnings || [];
  const needsOverride = warnings.some(
    (warning) => warning.code === "missing_unsubscribe_link",
  );

  return (
    <Box sx={{ p: 2, width: "100%" }}>
      <Typography variant="h6" gutterBottom>
        Launch or schedule a validated sending
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel>Campaign</InputLabel>
          <Select
            label="Campaign"
            value={campaignId}
            onChange={(event) => selectCampaign(event.target.value)}
          >
            {campaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 240 }} disabled={!campaignId}>
          <InputLabel>Sending</InputLabel>
          <Select
            label="Sending"
            value={sending?.id || ""}
            onChange={(event) => selectSending(event.target.value)}
          >
            {sendings.map((row) => (
              <MenuItem key={row.id} value={row.id}>
                {row.name} ({row.state})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiTextField
          type="datetime-local"
          label="Schedule (browser timezone)"
          value={scheduledFor}
          onChange={(event) => setScheduledFor(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>
      {sending && (
        <Box sx={{ mt: 2 }}>
          <Typography>
            Validation: {sending.latest_validation?.status || "not run"};{" "}
            {sending.validation_is_current ? "current" : "stale"}; effective
            recipients:{" "}
            {sending.latest_validation?.summary?.effective_recipients || 0}
          </Typography>
          {/* Temporal supplies actionable copy so operators see more than an internal warning code. */}
          {warnings.map((warning, index) => (
            <Alert key={index} severity="warning" sx={{ mt: 2 }}>
              {warning.message || warning.code} (template {warning.template_id})
            </Alert>
          ))}
          {needsOverride && (
            <MuiTextField
              fullWidth
              multiline
              minRows={2}
              label="Required unsubscribe-warning override reason"
              helperText="Explain why this email can be sent without an email-preferences link using {{ system.email_preferences_url }}."
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              sx={{ mt: 2 }}
            />
          )}
          <Button
            variant="contained"
            color="success"
            disabled={
              !sending.validation_is_current ||
              sending.has_active_run ||
              (needsOverride && !overrideReason.trim())
            }
            onClick={launch}
            sx={{ mt: 2 }}
          >
            {scheduledFor ? "Schedule run" : "Launch run"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function RunCreate() {
  return (
    <Create title="Create Email Run">
      <RunLauncher />
    </Create>
  );
}

// Run snapshots are immutable; this edit route exposes operational actions and recipient reporting only.
function RunReport() {
  const run = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [recipients, setRecipients] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [filters, setFilters] = useState({
    email: "",
    state: "",
    assignment_id: "",
  });
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadRecipients = async (requestedPage = page) => {
    const query = new URLSearchParams({
      _start: String((requestedPage - 1) * recipientsPerPage),
      _end: String(requestedPage * recipientsPerPage),
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const response = await authClient.get(
      `/admin/bulk-email/runs/${run.id}/recipients?${query}`,
    );
    setRecipients(response.json);
    setTotal(Number(response.headers.get("x-total-count") || 0));
    setPage(requestedPage);
    setSelected([]);
  };

  useEffect(() => {
    const query = new URLSearchParams({
      _start: "0",
      _end: String(recipientsPerPage),
    });
    Promise.all([
      authClient.get(`/admin/bulk-email/runs/${run.id}/recipients?${query}`),
      authClient.get(`/admin/bulk-email/runs/${run.id}/analytics`),
    ])
      .then(([recipientResponse, analyticsResponse]) => {
        setRecipients(recipientResponse.json);
        setTotal(Number(recipientResponse.headers.get("x-total-count") || 0));
        setAnalytics(analyticsResponse.json);
      })
      .catch((error) =>
        notify(error.body?.message || error.message, { type: "error" }),
      );
  }, [run.id, notify]);

  const retry = async () => {
    const selectedRows = recipients.filter((row) => selected.includes(row.id));
    const hasUnknown = selected.length
      ? selectedRows.some((row) => row.state === "unknown")
      : filters.state === "unknown" ||
        (!filters.state && run.unknown_count > 0);
    if (
      hasUnknown &&
      !window.confirm(
        "Some outcomes are unknown. Retrying may duplicate an accepted email. Continue?",
      )
    ) {
      return;
    }
    try {
      const response = await authClient.post(
        `/admin/bulk-email/runs/${run.id}/retries`,
        {
          recipient_ids: selected,
          state: filters.state || undefined,
          email: filters.email || undefined,
          assignment_id: filters.assignment_id || undefined,
          confirm_duplicate_risk: hasUnknown,
        },
      );
      notify("Retry run created", { type: "success" });
      redirect("edit", bulkEmailResources.runs, response.json.id);
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this run before dispatch?")) return;
    try {
      await authClient.post(`/admin/bulk-email/runs/${run.id}/cancel`, {});
      notify("Run canceled", { type: "success" });
      refresh();
    } catch (error) {
      notify(error.body?.message || error.message, { type: "error" });
    }
  };

  const exportCsv = async () => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const response = await fetch(
      apiURL(`/admin/bulk-email/runs/${run.id}/recipients/export?${query}`),
      { headers: { Authorization: `Bearer ${authToken()}` } },
    );
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `bulk-email-run-${run.id}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">
        Run #{run.id}: {run.state}
      </Typography>
      <Typography>
        Recipients {run.recipient_count || 0}; sent {run.sent_count || 0};
        failed {run.failed_count || 0}; unknown {run.unknown_count || 0}
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 2 }}>
        <MuiTextField
          label="Email"
          value={filters.email}
          onChange={(event) =>
            setFilters({ ...filters, email: event.target.value })
          }
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.state}
            onChange={(event) =>
              setFilters({ ...filters, state: event.target.value })
            }
          >
            <MenuItem value="">All</MenuItem>
            {recipientStates.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <MuiTextField
          label="Assignment"
          value={filters.assignment_id}
          onChange={(event) =>
            setFilters({ ...filters, assignment_id: event.target.value })
          }
        />
        <Button onClick={() => loadRecipients(1)}>Filter</Button>
        <Button onClick={exportCsv}>CSV</Button>
        <Button color="warning" onClick={retry}>
          Retry failures
        </Button>
        <Button color="error" onClick={cancel}>
          Cancel
        </Button>
      </Box>
      {analytics.map((row) => (
        <Typography key={row.assignment_id}>
          Assignment {row.assignment_id}: delivered {row.delivered}, opened{" "}
          {row.opened}, clicked {row.clicked}
        </Typography>
      ))}
      <Box sx={{ mt: 2 }}>
        {recipients.map((row) => (
          <Box key={row.id} sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              disabled={!["failed", "unknown"].includes(row.state)}
              checked={selected.includes(row.id)}
              onChange={(event) =>
                setSelected(
                  event.target.checked
                    ? [...selected, row.id]
                    : selected.filter((id) => id !== row.id),
                )
              }
            />
            <Typography>
              {row.normalized_email} — assignment {row.assignment_id} —{" "}
              {row.state} {row.failure_detail}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}>
        <Button disabled={page === 1} onClick={() => loadRecipients(page - 1)}>
          Previous
        </Button>
        <Typography>
          Page {page} of {Math.max(Math.ceil(total / recipientsPerPage), 1)} (
          {total} rows)
        </Typography>
        <Button
          disabled={page * recipientsPerPage >= total}
          onClick={() => loadRecipients(page + 1)}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

function RunEdit() {
  return (
    <Edit mutationMode="pessimistic">
      <RunReport />
    </Edit>
  );
}

export const BulkEmailRuns = {
  category: "email",
  name: bulkEmailResources.runs,
  list: RunList,
  create: RunCreate,
  edit: RunEdit,
  icon: Activity,
  roles: ["administrator"],
  options: { label: "Email Runs" },
};
